import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { HeroBase, HeroCommonExtras, AnimatedHeadlineWords, cmsUrl } from './HeroBase';

// Split-screen hero: copy on the left, big photo on the right.
// Inherits bg-image / confetti / location / pills / trust-badges from HeroBase.
export const HeroSplit = ({ cms = {}, onPrimary, onOpenAvailability }) => {
  const v = cms.variants?.split || {};
  const badgeText  = cms.badge_text  || 'Creative Birthdays · Oakville';
  const primaryCta = cms.primary_cta_text   || 'Build Your Dream Party';
  const secondCta  = cms.secondary_cta_text || 'Check Availability';
  const extras = HeroCommonExtras({ cms });

  return (
    <HeroBase
      cms={cms}
      fallbackBg="#FFFDF8"
      className="py-12 sm:py-20 lg:py-24"
      testidVariant="split"
    >
      <div className="container mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative z-10">
        {/* Left copy */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#FF3B6B] bg-[#FFEFF5] rounded-full px-3 py-1 mb-5" data-testid="hero-badge">
            <span aria-hidden>🎈</span> {badgeText}
          </span>
          <AnimatedHeadlineWords
            cms={cms}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-5"
          />
          {extras.sub}
          {extras.location}
          {extras.pills({ when: 'above' })}
          <div className="flex flex-wrap gap-3">
            <Button onClick={onPrimary} size="lg" className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-7" data-testid="hero-primary-cta">
              <Calendar className="w-5 h-5 mr-2" /> {primaryCta}
            </Button>
            <Button onClick={onOpenAvailability} variant="outline" size="lg" className="rounded-full px-7" data-testid="hero-secondary-cta">
              <CalendarDays className="w-5 h-5 mr-2" /> {secondCta}
            </Button>
          </div>
          {extras.pills({ when: 'below' })}
          {extras.trustBadges}
        </motion.div>

        {/* Right photo card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, rotate: 0 }}
          animate={{ opacity: 1, scale: 1, rotate: 1.2 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#FFDEE9] to-[#B5FFFC]">
            {v.right_image_url ? (
              <img src={cmsUrl(v.right_image_url)} alt={v.right_caption || 'Party'} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-7xl" aria-hidden>🎂</div>
            )}
            {v.right_caption && (
              <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur-sm rounded-2xl px-3 py-2 text-sm font-medium text-[#1E1E24] text-center">
                {v.right_caption}
              </div>
            )}
          </div>
          {v.decor_emoji && (
            <motion.span
              className="absolute -top-4 -right-2 text-4xl"
              animate={{ rotate: [0, 12, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden
            >{v.decor_emoji}</motion.span>
          )}
        </motion.div>
      </div>
    </HeroBase>
  );
};

export default HeroSplit;
